/**
 * ChargeStateMachine — hold-to-charge, release-to-fire, cancel.
 *
 * Three identical state machines (mouse LMB, spacebar, gamepad RT) share
 * this logic. Each tracks its own state; callers wire up DOM/gamepad events
 * and call start/cancel/release/reset/rearm.
 *
 * State flow:
 *   idle → start() → charging → release() → fire pulse → idle
 *   charging → cancel() → cancelled → release() → idle (no fire)
 *   any → reset() → idle (clears CHARGE key)
 */

import { Input } from 'bonkjs';
import { ACTION } from './constants';

export class ChargeStateMachine {
  state: 'idle' | 'charging' | 'cancelled' = 'idle';
  held = false;

  /** Begin charging (e.g. LMB down, Space down, RT pressed). */
  start(): void {
    this.held = true;
    if (this.state === 'idle') {
      this.state = 'charging';
      Input.setVirtualKey(ACTION.CHARGE, true);
    }
  }

  /** Cancel an active charge (e.g. RMB down, B button). */
  cancel(): void {
    if (this.state === 'charging') {
      this.state = 'cancelled';
      Input.setVirtualKey(ACTION.CHARGE, false);
    }
  }

  /** Release the trigger (e.g. LMB up, Space up, RT released).
   *  If still charging, pulses FIRE for 67ms. Always returns to idle. */
  release(): void {
    this.held = false;
    if (this.state === 'charging') {
      Input.setVirtualKey(ACTION.CHARGE, false);
      Input.setVirtualKey(ACTION.FIRE, true);
      setTimeout(() => Input.setVirtualKey(ACTION.FIRE, false), 67);
    }
    this.state = 'idle';
  }

  /** Force-reset to idle (e.g. leaving aiming phase). Clears CHARGE if active. */
  reset(): void {
    if (this.state !== 'idle') {
      this.state = 'idle';
      Input.setVirtualKey(ACTION.CHARGE, false);
    }
  }

  /** Re-arm: if the trigger is physically held and we're idle, start charging.
   *  Used on aiming entry to catch held triggers that fired mousedown during round_start. */
  rearm(): void {
    if (this.held && this.state === 'idle') {
      this.state = 'charging';
      Input.setVirtualKey(ACTION.CHARGE, true);
    }
  }
}
